import type { LockFileObject } from '@yarnpkg/lockfile';
import type { ResolvedVersionSpec, ResolvedVersions } from './types/ResolvedVersions.js';
import { makeUrl } from '@ms-cloudpack/path-string-parsing';

/**
 * Process yarn.lock file contents into a map of resolved package name+version.
 */
export function processYarnLock(lockFile: LockFileObject): ResolvedVersions {
  const installedVersions: ResolvedVersions = {};

  // Examples of LockFileObject entries:
  //   'prettier-2@npm:prettier@2': {
  //     version: '2.8.8',
  //     resolved: 'https://registry.yarnpkg.com/prettier/-/prettier-2.8.8.tgz#e8c5d7e98a4305ffe3de2e1fc4aca1a71c28b1da',
  //   },
  //   'weird-stuff@file:weird-stuff': { version: '1.0.0' },
  //   'zod@^3.21.4': {
  //     version: '3.22.4',
  //     resolved: 'https://registry.yarnpkg.com/zod/-/zod-3.22.4.tgz#f31c3a9386f61b1f228af56faa9255e845cf3fff',
  //   },
  for (const [spec, { version, resolved }] of Object.entries(lockFile)) {
    const atIndex = spec.indexOf('@', 1);
    const name = spec.slice(0, atIndex);
    const specVersion = spec.slice(atIndex + 1);
    const specEntry: ResolvedVersionSpec = { specVersion };
    const installedSpec = `${name}@${version}` as const;
    installedVersions[installedSpec] ??= { name, version, specs: [] };
    installedVersions[installedSpec].specs.push(specEntry);

    if (resolved) {
      // https://registry.yarnpkg.com/@aashutoshrathi/word-wrap/-/word-wrap-1.2.6.tgz#bd9154aec9983f77b3a034ecaa015c2e4201f6cf
      // https://registry.yarnpkg.com/yargs/-/yargs-16.2.0.tgz#1c82bf0f6b6a66eafce7ef30e376f49a12477f66
      // https://registry.yarnpkg.com/@azure/core-tracing/-/core-tracing-1.0.0-preview.13.tgz#55883d40ae2042f6f1e12b17dd0c0d34c536d644
      // https://example.pkgs.visualstudio.com/_packaging/Example/npm/registry/@foo/bar/-/bar-1.9.2.tgz#55883d40ae2042f6f1e12b17dd0c0d34c536d644
      const segments = makeUrl(resolved).pathname.split('/');
      specEntry.url = resolved;
      const [scope, unscopedName, dash, file] = segments.slice(-4);
      if (dash === '-' && file === `${unscopedName}-${version}.tgz`) {
        specEntry.realName = scope.startsWith('@') ? `${scope}/${unscopedName}` : unscopedName;
      }
    }
  }

  return installedVersions;
}
